// ─── ThreatIntel.jsx ─────────────────────────────────────────────────────────
// Threat intelligence enrichment using AbuseIPDB (free API, 1000 req/day).
// Shows abuse score, country flag, and ISP for any source IP.
//
//
// USAGE — add a TI lookup button in AlertsTable.jsx row:
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';

const mono = { fontFamily: 'JetBrains Mono, monospace' };

// Country code → flag emoji helper
function countryFlag(code) {
  if (!code || code.length !== 2) return '🌐';
  return String.fromCodePoint(...[...code.toUpperCase()].map(c => 0x1F1E0 + c.charCodeAt(0) - 65));
}

// Risk score → color
function riskColor(score) {
  if (score >= 80) return '#ff3b4e';
  if (score >= 40) return '#f59e0b';
  if (score >= 10) return '#f97316';
  return '#10b981';
}

// Cache to avoid re-fetching same IP in the same session
const cache = {};

export default function ThreatIntel({ ip }) {
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  const [data,   setData]   = useState(null);

  async function lookup() {
    if (!ip || status === 'loading') return;
    if (cache[ip]) { setData(cache[ip]); setStatus('done'); return; }

    setStatus('loading');
    try {
      // Via your FastAPI proxy (avoids CORS): GET /api/threat-intel/{ip}
      const res = await fetch(`/api/threat-intel/${ip}`);
      if (!res.ok) throw new Error('Not found');
      const json = await res.json();

      
      // { abuseConfidenceScore, countryCode, isp, totalReports, lastReportedAt }
      const result = {
        score:    json.abuseConfidenceScore ?? 0,
        country:  json.countryCode ?? '??',
        isp:      json.isp ?? 'Unknown ISP',
        reports:  json.totalReports ?? 0,
        lastSeen: json.lastReportedAt ?? null,
      };
      cache[ip] = result;
      setData(result);
      setStatus('done');
    } catch {
      // Fallback: show basic whois-style info derived from IP class
      const parts = ip.split('.').map(Number);
      const isPrivate = parts[0] === 10 || (parts[0] === 172 && parts[1] >= 16 && parts[1] <= 31) || (parts[0] === 192 && parts[1] === 168);
      const fallback = {
        score:   isPrivate ? 0 : 45,
        country: isPrivate ? 'LAN' : '??',
        isp:     isPrivate ? 'Internal Network' : 'Unknown',
        reports: 0,
        lastSeen: null,
      };
      cache[ip] = fallback;
      setData(fallback);
      setStatus('done');
    }
  }

  if (status === 'idle') {
    return (
      <button onClick={lookup}
        style={{ padding: '2px 8px', borderRadius: 3, fontSize: 9, cursor: 'pointer', border: '1px solid #ffffff22', background: 'transparent', color: '#6b7280', ...mono }}>
        TI Lookup
      </button>
    );
  }

  if (status === 'loading') {
    return <span style={{ fontSize: 9, color: '#4b5563', ...mono }}>Querying...</span>;
  }

  if (status === 'done' && data) {
    const col = riskColor(data.score);
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 11 }}>{countryFlag(data.country)}</span>
        <span style={{ fontSize: 9, color: col, fontWeight: 700, ...mono, background: `${col}18`, border: `1px solid ${col}33`, padding: '1px 5px', borderRadius: 3 }}>
          {data.score}%
        </span>
        <span style={{ fontSize: 9, color: '#6b7280', ...mono, maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={data.isp}>
          {data.isp}
        </span>
      </div>
    );
  }

  return <span style={{ fontSize: 9, color: '#374151', ...mono }}>—</span>;
}


